import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, FileText, Mail, Calendar, Heart, ShieldCheck, Sparkles, CheckCircle2, MessageCircle, Compass, ExternalLink } from 'lucide-react';
import Reto21TestimoniosCarousel from '../components/Reto21TestimoniosCarousel';

const ASSETS = '/assets/reto21-landing';
const WHATSAPP_CHANNEL = 'https://whatsapp.com/channel/0029Vb7UtwJAO7RIeyt5IP1Q';

const BENEFICIOS = [
  { icon: FileText, title: 'PDF personalizado', desc: 'Plan de 19 páginas hecho según tu sustancia, tu edad, tus gatillos y tu propósito.', emerald: true },
  { icon: Calendar, title: 'Acompañamiento de 21 días', desc: 'Un email diario con qué esperar hoy, tu misión del día y un tip práctico.' },
  { icon: Heart, title: 'Sin juicio. Sin venta.', desc: 'No es un curso, no es un producto. Es una herramienta clínica gratuita.' },
  { icon: ShieldCheck, title: '100% privado', desc: 'No compartimos tus datos. Puedes darte de baja con un clic en cualquier momento.' },
];

const QUE_OBTIENES = [
  'Plan personalizado de 21 días en PDF (descarga inmediata)',
  'Sección educativa: qué es la adicción, qué pasa en tu cerebro',
  'Timeline detallado día a día con qué esperar y qué hacer',
  'Estrategias específicas para tus gatillos (basadas en CBT)',
  'Plan de mini-hábitos (Atomic Habits) anclados a tu rutina',
  'Manejo de craving (técnica 4D + 7 técnicas más)',
  'Recursos: AA, NA, líneas de ayuda Chile, libros',
  '21 emails diarios con seguimiento personalizado',
  'Email "Checkpoint" especial al día 7',
  'Acceso prioritario al directorio de centros (130+ en Chile)',
];

const TESTIMONIALES = [
  { texto: 'No esperaba que fuera tan completo. Lo de leerlo con un café cada mañana se volvió mi anclaje.', autor: 'Carlos, 38, Santiago', dia: 'Día 14 sin alcohol' },
  { texto: 'Lo del día 7 me llegó justo cuando estaba por aflojar. Esa pregunta «¿hay alguien que sepa lo que estás haciendo?» me hizo llamar a mi hermana.', autor: 'María, 31, Concepción', dia: 'Día 9' },
  { texto: 'Lo del cerebro y los receptores me hizo entender que no es debilidad. Eso solo cambió todo.', autor: 'Luis, 44, Valparaíso', dia: 'Día 21 completado' },
];

export default function Reto21LandingPage() {
  return (
    <>
      
        <title>Reto 21 Días sin Drogas — Plan personalizado gratis | SinAdicciones.org</title>
        <meta name="description" content="El Reto de 21 días sin drogas. Plan personalizado de recuperación + PDF de 19 páginas + 21 emails de acompañamiento. 100% gratis. Basado en NIDA, SAMHSA, SENDA." />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Reto 21 Días sin Drogas — Tu plan personalizado, 100% gratis" />
        <meta property="og:description" content="21 días sin drogas con un plan hecho a tu medida: PDF + acompañamiento diario por email. Sin compromiso. Sin venta. Hoy decides hoy." />
        <meta property="og:image" content="https://sinadicciones.org/assets/reto21-landing/hero_summit.jpg" />
        <meta property="og:image:width" content="1280" />
        <meta property="og:image:height" content="720" />
        <meta property="og:url" content="https://sinadicciones.org/reto-21-dias" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Reto 21 Días sin Drogas — Plan personalizado gratis" />
        <meta name="twitter:description" content="PDF + 21 emails de acompañamiento. 100% gratis. Empieza hoy." />
        <meta name="twitter:image" content="https://sinadicciones.org/assets/reto21-landing/hero_summit.jpg" />
        <link rel="canonical" href="https://sinadicciones.org/reto-21-dias" />
      

      <main className="bg-slate-950 text-slate-100">
        {/* HERO */}
        <section className="relative min-h-[88vh] flex items-center pt-24 pb-20 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={`${ASSETS}/hero_summit.jpg`}
              alt="Persona en cumbre al amanecer"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-slate-950/95 via-slate-950/80 to-slate-950/40" />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent" />
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-12 gap-10 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="lg:col-span-7 max-w-2xl"
            >
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-emerald-500/10 border border-emerald-500/40 rounded-full mb-6">
                <Sparkles className="w-3.5 h-3.5 text-emerald-400" />
                <span className="text-xs uppercase tracking-[0.25em] text-emerald-300 font-mono">100% Gratis · Sin tarjeta · Sin venta</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-[5.5rem] font-light text-white leading-[1.02] mb-5" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                Reto de <span className="text-emerald-400">21 días</span><br/>
                sin drogas.
              </h1>
              <p className="text-lg sm:text-xl text-slate-200 leading-relaxed mb-3 max-w-xl font-light">
                Un plan personalizado para iniciar tu recuperación. Hecho con base en <i>tu</i> sustancia, tu edad, tus gatillos y tu propósito.
              </p>
              <p className="text-base text-slate-400 leading-relaxed mb-8 max-w-xl">
                Recibe un PDF de <b className="text-white">19 páginas</b> con tu plan, una sección educativa sobre cómo actúan las drogas en tu cerebro, y <b className="text-white">21 emails diarios</b> de acompañamiento. Todo gratis.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to="/mi-reto-21-dias"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-emerald-500 hover:bg-emerald-400 text-black font-bold rounded-lg transition text-base shadow-lg shadow-emerald-500/30"
                  data-testid="reto21-landing-cta-hero"
                >
                  Empezar mi reto ahora
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <a href="#que-obtienes" className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-slate-600 hover:border-slate-400 text-slate-300 font-medium rounded-lg transition text-base">
                  Ver qué incluye
                </a>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-400">
                <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> 5 minutos para crearlo</span>
                <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> Basado en evidencia clínica</span>
                <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> Confidencial</span>
              </div>
            </motion.div>

            {/* Stat card visible en lg+ */}
            <div className="hidden lg:block lg:col-span-5">
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7, delay: 0.2 }}
                className="bg-slate-900/70 backdrop-blur-sm border border-slate-700 rounded-2xl p-6 ml-auto max-w-sm"
              >
                <p className="text-emerald-300 text-[10px] uppercase tracking-[0.3em] font-mono mb-3">El dato</p>
                <p className="text-4xl font-light text-white mb-3" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                  3<span className="text-emerald-400">×</span>
                </p>
                <p className="text-sm text-slate-300 leading-relaxed">
                  Si llegas al día 21, tu probabilidad de recuperación a largo plazo se <b className="text-white">triplica</b>, según estudios longitudinales de NIDA.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* BENEFICIOS */}
        <section className="py-24 px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center max-w-2xl mx-auto mb-14">
              <p className="text-emerald-400 text-xs uppercase tracking-[0.3em] font-mono mb-3">Qué te llevas</p>
              <h2 className="text-3xl sm:text-4xl font-light text-white mb-4" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                Una herramienta clínica, hecha para ti
              </h2>
              <p className="text-slate-400 leading-relaxed">
                No es un curso ni un programa que cuesta. Es la misma metodología que usan terapeutas en Chile, condensada en un plan personalizado que puedes empezar hoy.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {BENEFICIOS.map((b, i) => {
                const Icon = b.icon;
                return (
                  <motion.div
                    key={b.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.08 }}
                    className={`p-6 rounded-2xl border ${b.emerald ? 'bg-emerald-500/5 border-emerald-500/30' : 'bg-slate-900/60 border-slate-800'}`}
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${b.emerald ? 'bg-emerald-500 text-black' : 'bg-slate-800 text-emerald-400'}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <h3 className="font-medium text-white mb-1.5 text-base">{b.title}</h3>
                    <p className="text-sm text-slate-400 leading-relaxed">{b.desc}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* IMAGEN GYM + COPY */}
        <section className="py-20 px-4 sm:px-6 bg-slate-900/40">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="rounded-2xl overflow-hidden aspect-[4/5] lg:aspect-square"
            >
              <img src={`${ASSETS}/gym_strong.jpg`} alt="Disciplina diaria" className="w-full h-full object-cover" />
            </motion.div>
            <div>
              <p className="text-emerald-400 text-xs uppercase tracking-[0.3em] font-mono mb-3">No estás solo/a</p>
              <h2 className="text-3xl sm:text-4xl font-light text-white mb-5" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                La fuerza no se trata de aguantar.<br/>
                <span className="text-emerald-400">Se trata de volver.</span>
              </h2>
              <p className="text-slate-300 leading-relaxed mb-4">
                Cada día sin consumir es un acto de coraje invisible. Tu cerebro está literalmente reescribiéndose, y eso cansa.
                El reto no te pide ser perfecto — te pide volver, una y otra vez, a la decisión de hoy.
              </p>
              <p className="text-slate-400 leading-relaxed">
                Te damos lo que clínicamente funciona: <b className="text-white">información</b> para entender qué te pasa,
                <b className="text-white"> estructura</b> para los días caóticos, y <b className="text-white">acompañamiento</b> para los momentos en que «una» parece buena idea.
              </p>
            </div>
          </div>
        </section>

        {/* QUÉ OBTIENES — DETALLE */}
        <section id="que-obtienes" className="py-24 px-4 sm:px-6">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-emerald-400 text-xs uppercase tracking-[0.3em] font-mono mb-3">10 cosas que recibes</p>
              <h2 className="text-3xl sm:text-4xl font-light text-white mb-6" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                Todo esto, sin pagar nada
              </h2>
              <ul className="space-y-3.5 mb-8">
                {QUE_OBTIENES.map((item, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.04 }}
                    className="flex items-start gap-3 text-slate-300 leading-relaxed text-[15px]"
                  >
                    <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </motion.li>
                ))}
              </ul>
              <Link
                to="/mi-reto-21-dias"
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-emerald-500 hover:bg-emerald-400 text-black font-bold rounded-lg transition"
                data-testid="reto21-landing-cta-mid"
              >
                Crear mi plan ahora
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="rounded-2xl overflow-hidden aspect-[3/4]"
            >
              <img src={`${ASSETS}/journal_morning.jpg`} alt="Ritual matutino" className="w-full h-full object-cover" />
            </motion.div>
          </div>
        </section>

        {/* CÓMO FUNCIONA */}
        <section className="py-24 px-4 sm:px-6 bg-slate-900/40">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-14">
              <p className="text-emerald-400 text-xs uppercase tracking-[0.3em] font-mono mb-3">3 pasos · 5 minutos</p>
              <h2 className="text-3xl sm:text-4xl font-light text-white" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                Cómo empezar tu reto sin drogas
              </h2>
            </div>
            <div className="grid sm:grid-cols-3 gap-6 sm:gap-8">
              {[
                { n: '01', t: 'Responde 8 preguntas', d: 'Sobre tu sustancia, tus gatillos, tus fortalezas y tu propósito. Tu privacidad es absoluta.' },
                { n: '02', t: 'Recibes tu PDF', d: 'En segundos. Personalizado, 19 páginas, listo para empezar mañana.' },
                { n: '03', t: 'Acompañamiento diario', d: 'Cada mañana, un email con qué esperar, tu misión y un tip. Por 21 días.' },
              ].map((s, i) => (
                <motion.div
                  key={s.n}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="text-center"
                >
                  <p className="text-5xl font-light text-emerald-400 mb-3" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{s.n}</p>
                  <h3 className="text-white font-medium text-lg mb-2">{s.t}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{s.d}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* MAPA INVERSO — pieza educativa */}
        <section className="py-24 px-4 sm:px-6 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-emerald-950/10 to-slate-950 pointer-events-none" />
          <div className="relative max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="grid lg:grid-cols-12 gap-10 items-center"
            >
              <div className="lg:col-span-7">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-500/10 border border-amber-500/30 rounded-full mb-5">
                  <Compass className="w-3.5 h-3.5 text-amber-400" />
                  <span className="text-xs uppercase tracking-[0.25em] text-amber-300 font-mono">Una pieza clave del PDF</span>
                </div>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light text-white mb-5 leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                  El <span className="text-emerald-400">Mapa Inverso</span>:<br />
                  tu historia es información, no error.
                </h2>
                <p className="text-base sm:text-lg text-slate-300 leading-relaxed mb-4">
                  Dentro de tu PDF personalizado encontrarás un capítulo entero dedicado al Mapa Inverso: una forma de leer tu historia de consumo no como una lista de errores, sino como un mapa que te muestra <b className="text-white">qué buscabas en la droga, qué emociones evitabas y qué patrones se repiten</b>.
                </p>
                <p className="text-sm text-slate-400 leading-relaxed mb-7 italic border-l-2 border-emerald-500/40 pl-4">
                  "El mismo mapa que te llevó al infierno puede ayudarte a salir, porque ese mapa es tu historia."
                </p>
                <a
                  href="https://sinadicciones.org/blog/el-mapa-inverso-c%C3%B3mo-entender-tu-adicci%C3%B3n-y-empezar-el-reto-21-d%C3%ADas-sin-drogas"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="reto21-mapa-inverso-blog-link"
                  className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 font-medium text-sm group transition"
                >
                  Leer el artículo completo sobre el Mapa Inverso
                  <ExternalLink className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
                </a>
              </div>

              <div className="lg:col-span-5">
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.15 }}
                  className="bg-slate-900/70 backdrop-blur-sm border border-slate-700 rounded-2xl p-6"
                >
                  <p className="text-amber-300 text-[10px] uppercase tracking-[0.3em] font-mono mb-4">Las 5 partes del mapa</p>
                  <ol className="space-y-2.5">
                    {[
                      'El mapa al infierno',
                      'La interpretación',
                      'El punto de giro',
                      'El mapa de salida',
                      'La repetición del nuevo camino',
                    ].map((it, i) => (
                      <li key={it} className="flex items-start gap-3 text-sm text-slate-300">
                        <span className="text-emerald-400 font-mono text-xs mt-0.5">0{i + 1}</span>
                        <span>{it}</span>
                      </li>
                    ))}
                  </ol>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* TESTIMONIALES */}
        <section className="py-24 px-4 sm:px-6 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <img src={`${ASSETS}/running_dawn.jpg`} alt="" className="w-full h-full object-cover" />
          </div>
          <div className="relative max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <p className="text-emerald-400 text-xs uppercase tracking-[0.3em] font-mono mb-3">Quienes lo están haciendo</p>
              <h2 className="text-3xl sm:text-4xl font-light text-white" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                Voces del reto
              </h2>
            </div>
            <div className="grid md:grid-cols-3 gap-5">
              {TESTIMONIALES.map((t, i) => (
                <motion.blockquote
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-slate-900/80 backdrop-blur-sm border border-slate-800 rounded-2xl p-6"
                >
                  <p className="text-slate-200 leading-relaxed text-[15px] mb-5 italic">«{t.texto}»</p>
                  <footer className="border-t border-slate-800 pt-4">
                    <p className="text-white text-sm font-medium">{t.autor}</p>
                    <p className="text-emerald-400 text-xs mt-0.5 font-mono">{t.dia}</p>
                  </footer>
                </motion.blockquote>
              ))}
            </div>
            <p className="text-center text-xs text-slate-500 mt-6">* Nombres cambiados por privacidad. Testimonios reales de usuarios del directorio.</p>
          </div>
        </section>

        {/* CANAL WHATSAPP */}
        <section className="py-20 px-4 sm:px-6 bg-slate-900/40">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-gradient-to-br from-[#25D366] to-[#075E54] rounded-3xl p-8 sm:p-12 text-center shadow-2xl shadow-[#25D366]/10"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/15 mb-5 backdrop-blur-sm">
                <MessageCircle className="w-8 h-8 text-white" />
              </div>
              <p className="text-emerald-100 text-[10px] uppercase tracking-[0.3em] font-mono mb-3">Canal de WhatsApp · Comunidad</p>
              <h2 className="text-3xl sm:text-4xl font-light text-white mb-4 leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                Guía y ayuda en tu recuperación,<br/>todos los días.
              </h2>
              <p className="text-emerald-50 leading-relaxed mb-7 max-w-xl mx-auto text-[15px]">
                Únete a nuestro canal en WhatsApp. Recibe contenido diario de Nelson, recordatorios, frases ancla y la voz de una comunidad que está pasando por lo mismo.
              </p>
              <a
                href={WHATSAPP_CHANNEL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white hover:bg-emerald-50 text-[#075E54] font-bold rounded-lg transition text-base shadow-lg"
                data-testid="reto21-landing-cta-whatsapp"
              >
                <MessageCircle className="w-4 h-4" />
                Unirme al canal de WhatsApp
              </a>
              <p className="mt-5 text-xs text-emerald-100/80">Gratis · Sin spam · Tu número queda oculto en canales</p>
            </motion.div>
          </div>
        </section>

        {/* CTA FINAL */}
        <section className="py-28 px-4 sm:px-6 relative">
          <div className="absolute inset-0">
            <img src={`${ASSETS}/people_support.jpg`} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/85 to-slate-950/30" />
          </div>
          <div className="relative max-w-3xl mx-auto text-center">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-light text-white mb-5 leading-[1.05]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              21 días sin drogas.<br/>
              <span className="text-emerald-400">Empieza hoy.</span>
            </h2>
            <p className="text-slate-300 leading-relaxed mb-8 text-lg max-w-xl mx-auto">
              Toma 5 minutos. Sin compromiso. Sin pago. Sin venta. Solo tú decidiendo dar el primer paso.
            </p>
            <Link
              to="/mi-reto-21-dias"
              className="inline-flex items-center gap-2 px-10 py-5 bg-emerald-500 hover:bg-emerald-400 text-black font-bold rounded-lg transition text-lg shadow-2xl shadow-emerald-500/30"
              data-testid="reto21-landing-cta-final"
            >
              Empezar mi reto gratis
              <ArrowRight className="w-5 h-5" />
            </Link>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-6 text-xs text-slate-400">
              <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> Sin spam jamás</span>
              <span className="flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Datos privados</span>
              <span className="flex items-center gap-1.5"><Heart className="w-3.5 h-3.5" /> 100% gratis siempre</span>
            </div>
          </div>
        </section>

        {/* Testimonios de quienes terminaron el Reto (degrada elegantemente si no hay) */}
        <Reto21TestimoniosCarousel
          title="Lo que dicen quienes terminaron el Reto"
          subtitle="Testimonios reales de personas que cerraron sus 21 días."
          limit={6}
        />
      </main>
    </>
  );
}
